import { CommonModule } from '@angular/common';
import { Component, DestroyRef, type OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { CdwIconModule } from '@cdw-ae/common-ui-components-icon';
import { Actions, ofType } from '@ngrx/effects';
import { firstValueFrom } from 'rxjs';
import * as PlatformIdentityActions from '../../../+state/platform-identity/platform-identity.actions';
import { environment } from '../../../../environments/environment';
import { PlatformAuthService } from '../../../common/services/platform-auth/platform-auth.service';

@Component({
  selector: 'splash-screen',
  imports: [CommonModule, CdwIconModule],
  templateUrl: './splash.component.html',
  styleUrl: './splash.component.scss',
})
export class SplashComponent implements OnInit {
  private readonly platformAuthService = inject(PlatformAuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly actions$ = inject(Actions);

  isLoading = false;
  loginError: string | null = null;
  /** The same per-environment allowlist the add-admin dialog shows. */
  readonly allowedDomains = environment.allowedAdminEmailDomains;
  readonly privacyUrl = environment.urls.legal.privacyNotice;
  readonly version = environment.version;
  readonly revision = environment.revision;

  /**
   * Environment indicator shown on the console (LOCAL / SANDBOX / DEVELOP /
   * PRODUCTION). Surfacing the environment is an intentional "internal tool"
   * cue. Derived at runtime so it needs no per-environment config: local and
   * sandbox share a Firebase project, so we distinguish local by its backend
   * URL before falling back to the project id.
   */
  get environmentLabel(): string {
    const api = environment.urls.backendApi ?? '';
    if (api.includes('localhost') || api.includes('127.0.0.1')) {
      return 'LOCAL';
    }
    const projectId = environment.firebase.projectId ?? '';
    if (projectId.includes('sandbox')) {
      return 'SANDBOX';
    }
    if (projectId.includes('develop')) {
      return 'DEVELOP';
    }
    if (environment.production || projectId.includes('prod')) {
      return 'PRODUCTION';
    }
    return 'NON-PROD';
  }

  ngOnInit(): void {
    // Primary channel for the common case (splash already mounted): every failed
    // validation dispatches `validateFailed`, so this fires on each attempt even
    // when the URL doesn't change (a second bad account → same-URL navigation
    // wouldn't re-emit a query param).
    this.actions$
      .pipe(
        ofType(PlatformIdentityActions.validateFailed),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ error }) => {
        this.loginError = error;
        this.isLoading = false;
      });

    // Secondary channel: when validation fails mid-session (token refresh on a
    // guarded page), the splash mounts fresh via /welcome?authError=… and reads
    // the message from the query param.
    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const authError = params.get('authError');
        if (authError) {
          this.loginError = decodeURIComponent(authError);
          this.isLoading = false;
        }
      });
  }

  async handleGoogleLogin(): Promise<void> {
    this.isLoading = true;
    this.loginError = null;

    try {
      // Popup only. The validate round-trip + navigation are driven by
      // onIdTokenChanged in PlatformAuthService and PlatformIdentityEffects.
      await firstValueFrom(this.platformAuthService.loginWithGoogle());
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Please try again.';
      this.loginError = `Login failed: ${message}`;
      this.isLoading = false;
    }
  }
}
